const printerServiceUuids = [
  '00001101-0000-1000-8000-00805f9b34fb',
  '00001101-0000-1000-8000-0002ee000001',
  '000018f0-0000-1000-8000-00805f9b34fb',
  '0000ffe0-0000-1000-8000-00805f9b34fb',
  '0000ff00-0000-1000-8000-00805f9b34fb',
  '0000ff12-0000-1000-8000-00805f9b34fb',
  '0000ff02-0000-1000-8000-00805f9b34fb'
];

const reconnectDelayMs = 5000;

function normalizeBluetoothErrorMessage(error, fallbackMessage) {
  const rawMessage = error?.message || '';

  if (rawMessage.includes('Unsupported device')) {
    return 'Perangkat yang dipilih tidak didukung oleh Web Bluetooth. Kemungkinan printer ini hanya mendukung Bluetooth Classic/SPP dan bukan BLE/GATT.';
  }

  if (rawMessage.includes('User cancelled the requestDevice chooser')) {
    return 'Pemilihan perangkat dibatalkan oleh user.';
  }

  if (rawMessage.includes('GATT Server is disconnected')) {
    return 'Perangkat tidak dapat dijangkau. Pastikan printer dalam jangkauan dan dinyalakan.';
  }

  return rawMessage || fallbackMessage;
}

const state = {
  bluetoothDevice: null,
  server: null,
  characteristic: null,
  isConnecting: false,
  error: null,
  isInitialized: false,
  isWriting: false,
  isPrintJobActive: false,
  userInitiatedDisconnect: false,
  reconnectTimeoutId: null,
  handleDisconnection: null
};

const mutations = {
  SET_DEVICE(state, device) {
    state.bluetoothDevice = device;
  },
  SET_SERVER(state, server) {
    state.server = server;
  },
  SET_CHARACTERISTIC(state, characteristic) {
    state.characteristic = characteristic;
  },
  SET_CONNECTING(state, isConnecting) {
    state.isConnecting = isConnecting;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  SET_INITIALIZED(state, value) {
    state.isInitialized = value;
  },
  SET_WRITING(state, value) {
    state.isWriting = value;
  },
  SET_PRINT_JOB_ACTIVE(state, value) {
    state.isPrintJobActive = value;
  },
  SET_USER_INITIATED_DISCONNECT(state, value) {
    state.userInitiatedDisconnect = value;
  },
  SET_RECONNECT_TIMEOUT_ID(state, timeoutId) {
    state.reconnectTimeoutId = timeoutId;
  },
  SET_HANDLE_DISCONNECTION(state, handler) {
    state.handleDisconnection = handler;
  },
  CLEAR_CONNECTION_STATE(state) {
    state.server = null;
    state.characteristic = null;
    state.isConnecting = false;
    state.isWriting = false;
    state.isPrintJobActive = false;
  },
  RESET_STATE(state) {
    state.bluetoothDevice = null;
    state.server = null;
    state.characteristic = null;
    state.isConnecting = false;
    state.error = null;
    state.isWriting = false;
    state.isPrintJobActive = false;
    state.userInitiatedDisconnect = false;
    state.reconnectTimeoutId = null;
    state.handleDisconnection = null;
  }
};

const actions = {
  clearReconnectTimeout({ state, commit }) {
    if (state.reconnectTimeoutId) {
      clearTimeout(state.reconnectTimeoutId);
      commit('SET_RECONNECT_TIMEOUT_ID', null);
    }
  },

  scheduleReconnect({ state, commit, dispatch }) {
    if (
      state.userInitiatedDisconnect ||
      state.reconnectTimeoutId ||
      !state.bluetoothDevice
    ) {
      return;
    }

    const timeoutId = setTimeout(async () => {
      commit('SET_RECONNECT_TIMEOUT_ID', null);

      if (state.userInitiatedDisconnect || !state.bluetoothDevice) {
        return;
      }

      const success = await dispatch('reconnectToDevice', state.bluetoothDevice);
      if (!success && !state.userInitiatedDisconnect) {
        dispatch('scheduleReconnect');
      }
    }, reconnectDelayMs);

    commit('SET_RECONNECT_TIMEOUT_ID', timeoutId);
  },

  async initializeBluetooth({ state, commit, dispatch }) {
    if (state.isInitialized || !navigator.bluetooth) return;

    commit('SET_INITIALIZED', true);

    try {
      const devices = await navigator.bluetooth.getDevices();
      if (devices.length > 0) {
        console.log('Found previously connected Bluetooth devices, attempting to reconnect...');
        await dispatch('reconnectToDevice', devices[0]);
      }
    } catch (error) {
      console.warn('Error initializing Bluetooth:', error);
    }
  },

  async reconnectToDevice({ state, commit, dispatch }, payload) {
    const device = payload?.device || payload;
    const skipConnectingGuard = Boolean(payload?.skipConnectingGuard);

    if (!device || !device.gatt) {
      console.error('Perangkat Bluetooth tidak valid atau tidak memiliki GATT');
      return false;
    }

    if (state.isConnecting && !skipConnectingGuard) {
      return false;
    }

    dispatch('clearReconnectTimeout');
    commit('SET_CONNECTING', true);
    commit('SET_ERROR', null);
    commit('SET_USER_INITIATED_DISCONNECT', false);

    if (state.handleDisconnection) {
      device.removeEventListener('gattserverdisconnected', state.handleDisconnection);
    }

    const handleDisconnect = () => {
      console.log(`Perangkat terputus: ${device.name || 'Tanpa Nama'}`);
      dispatch('handleDisconnection');
    };

    try {
      console.log(`Mencoba menyambung ke perangkat: ${device.name || 'Tanpa Nama'}`);

      device.addEventListener('gattserverdisconnected', handleDisconnect);
      commit('SET_HANDLE_DISCONNECTION', handleDisconnect);

      if (device.gatt.connected) {
        console.log('Sudah connect');
      }

      console.log('Sudah connect', await device.gatt.connect());
      let server;
      try {
        console.log('Menghubungkan ke server GATT...');
        server = await device.gatt.connect();
      } catch (error) {
        const message = normalizeBluetoothErrorMessage(
          error,
          'Gagal terhubung ke perangkat Bluetooth'
        );
        throw new Error(message);
      }

      let service = null;

      for (const serviceUuid of printerServiceUuids) {
        try {
          console.log(`Mencoba layanan dengan UUID: ${serviceUuid}`);
          service = await server.getPrimaryService(serviceUuid);
          console.log(`Berhasil menemukan layanan dengan UUID: ${serviceUuid}`);
          break;
        } catch (error) {
          console.log(`Layanan tidak ditemukan dengan UUID: ${serviceUuid}`, error);
        }
      }

      if (!service) {
        throw new Error('Tidak dapat menemukan layanan printer yang didukung. Pastikan perangkat adalah printer Bluetooth yang kompatibel.');
      }

      console.log('Mengambil karakteristik layanan...');
      const characteristics = await service.getCharacteristics();
      const characteristic = characteristics.find((item) =>
        item.properties.writeWithoutResponse || item.properties.write
      );

      if (!characteristic) {
        throw new Error('Tidak dapat menemukan karakteristik yang dapat ditulis. Perangkat mungkin bukan printer yang didukung.');
      }

      commit('SET_DEVICE', device);
      commit('SET_SERVER', server);
      commit('SET_CHARACTERISTIC', characteristic);

      console.log(`Berhasil terhubung ke printer: ${device.name || 'Tanpa Nama'}`);
      return true;
    } catch (error) {
      console.error('Kesalahan saat menyambung ulang ke perangkat:', error);

      commit('CLEAR_CONNECTION_STATE');
      commit('SET_ERROR', {
        message: normalizeBluetoothErrorMessage(
          error,
          'Gagal menyambung ke perangkat Bluetooth'
        ),
        originalError: error,
        deviceName: device?.name || 'Perangkat Tidak Dikenal'
      });

      if (device) {
        device.removeEventListener('gattserverdisconnected', handleDisconnect);
      }
      commit('SET_HANDLE_DISCONNECTION', null);

      return false;
    } finally {
      commit('SET_CONNECTING', false);
    }
  },

  async connectBluetooth({ state, commit, dispatch }) {
    if (state.bluetoothDevice && state.characteristic) return true;

    commit('SET_CONNECTING', true);
    commit('SET_ERROR', null);
    commit('SET_USER_INITIATED_DISCONNECT', false);
    dispatch('clearReconnectTimeout');

    try {
      if (!navigator.bluetooth) {
        throw new Error('Bluetooth is not supported in your browser.');
      }

      const device = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: printerServiceUuids,
      });

      if (!device || !device.name) {
        throw new Error('Could not connect to the specified Bluetooth device');
      }

      if (device.gatt.connected) {
        return true;
      }

      await new Promise(r => setTimeout(r, 300)); // delay kecil

      const success = await dispatch('reconnectToDevice', {
        device,
        skipConnectingGuard: true,
      });
      if (!success) {
        throw new Error('Failed to connect to the device');
      }

      return true;
    } catch (error) {
      console.error('Bluetooth connection error:', error);
      commit('SET_ERROR', {
        message: normalizeBluetoothErrorMessage(
          error,
          'Gagal menghubungkan perangkat Bluetooth'
        ),
        originalError: error,
      });
      throw error;
    } finally {
      commit('SET_CONNECTING', false);
    }
  },

  async disconnectBluetooth({ commit, state, dispatch }) {
    commit('SET_USER_INITIATED_DISCONNECT', true);
    dispatch('clearReconnectTimeout');

    if (state.bluetoothDevice && state.handleDisconnection) {
      state.bluetoothDevice.removeEventListener('gattserverdisconnected', state.handleDisconnection);
      commit('SET_HANDLE_DISCONNECTION', null);
    }

    if (state.server && state.server.connected) {
      try {
        state.server.disconnect();
      } catch (error) {
        console.error('Error disconnecting:', error);
      }
    }

    commit('RESET_STATE');
  },

  handleDisconnection({ state, commit, dispatch }) {
    console.log('Bluetooth device disconnected');

    if (state.userInitiatedDisconnect) {
      return;
    }

    commit('CLEAR_CONNECTION_STATE');
    commit('SET_ERROR', {
      message: 'Koneksi printer terputus. Mencoba menyambungkan ulang...',
      deviceName: state.bluetoothDevice?.name || 'Perangkat Tidak Dikenal'
    });

    dispatch('scheduleReconnect');
  },

  startPrinterJob({ commit }) {
    commit('SET_PRINT_JOB_ACTIVE', true);
  },

  endPrinterJob({ commit }) {
    commit('SET_PRINT_JOB_ACTIVE', false);
  },

  async sendKeepAlive({ state, dispatch }) {
    if (
      !state.characteristic ||
      state.isConnecting ||
      state.isWriting ||
      state.isPrintJobActive ||
      state.userInitiatedDisconnect
    ) {
      return false;
    }

    try {
      await dispatch('sendToPrinter', new Uint8Array([0x1B, 0x40]));
      console.log("kirim pesan wake up printer");
      return true;
    } catch (error) {
      console.warn('Failed to send printer keep-alive command:', error);
      return false;
    }
  },

  async sendToPrinter({ state, commit }, data) {
    if (!state.characteristic) {
      throw new Error('Not connected to a Bluetooth device');
    }

    commit('SET_WRITING', true);

    try {
      if (state.characteristic.writeValueWithoutResponse) {
        await state.characteristic.writeValueWithoutResponse(data);
      } else {
        await state.characteristic.writeValue(data);
      }
    } catch (error) {
      console.error('Error sending data to printer:', error);
      throw error;
    } finally {
      commit('SET_WRITING', false);
    }
  }
};

const getters = {
  isConnected: (state) => !!state.bluetoothDevice,
  deviceName: (state) => state.bluetoothDevice?.name || null,
  isConnecting: (state) => state.isConnecting,
  connectionError: (state) => state.error?.message || null,
  isInitialized: (state) => state.isInitialized,
  isWriting: (state) => state.isWriting,
  isPrintJobActive: (state) => state.isPrintJobActive
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
